/**
 * KX Insights route definitions.
 *
 * The route engine (HandleRouteComponent) turns these into Angular child routes
 * and resolves each `component` by its `selector` via the module's buildFactory.
 * Order matters: literal `bi_dashboard`/`designer` paths precede `:identifier` so
 * they aren't swallowed by the wildcard dashboard route.
 *
 * The engine always injects an empty-path redirect to `defaultApplication`
 * (= `bi_dashboard`, see app-routing.module.ts), so `/kxinsights` itself can
 * never render a component — the landing must live at the redirect target.
 *
 *   /kxinsights                 -> (redirect) -> /kxinsights/bi_dashboard
 *   /kxinsights/bi_dashboard    -> landing (list / manage dashboards)
 *   /kxinsights/designer        -> designer (new dashboard)
 *   /kxinsights/designer/<id>   -> designer (edit dashboard)
 *   /kxinsights/scheduling      -> alerts & subscriptions manager
 *   /kxinsights/catalog         -> data catalog (search / meaning / impact)
 *   /kxinsights/models          -> model registry, drift and rollback
 *   /kxinsights/<identifier>    -> viewer (render a dashboard)
 */
export const kxinsightsRouteDef = [
  {
    path: 'bi_dashboard',
    component: 'KxInsightsLandingComponent',
    data: { selector: 'app-kxinsights-landing', resource: 'bi_dashboard', action: 'LIST' },
  },
  {
    path: 'designer',
    component: 'KxInsightsDesignerComponent',
    data: { selector: 'app-kxinsights-designer', resource: 'bi_dashboard', action: 'ADD' },
  },
  {
    path: 'designer/:identifier',
    component: 'KxInsightsDesignerComponent',
    data: { selector: 'app-kxinsights-designer', resource: 'bi_dashboard', action: 'MODIFY' },
  },
  {
    path: 'scheduling',
    component: 'KxInsightsSchedulingComponent',
    data: { selector: 'app-kxinsights-scheduling', resource: 'bi_query', action: 'SCHEDULE_LIST' },
  },
  {
    // Browse every table and column across every source. `bi_query` +
    // CATALOG_SEARCH, so an admin can grant browsing widely while profiling
    // (which runs real queries) stays its own narrower right.
    path: 'catalog',
    component: 'KxInsightsCatalogComponent',
    data: { selector: 'app-kxinsights-catalog', resource: 'bi_query', action: 'CATALOG_SEARCH' },
  },
  {
    // The model lifecycle: what is predicting, which version is live, whether it
    // has drifted, and how to roll it back. `bi_query` + ML_MODELS, so viewing is
    // a wide right while ML_ALIAS (promote/roll back) and ML_TRAIN stay narrow —
    // reading which model is live is a very different thing from changing it.
    path: 'models',
    component: 'KxInsightsModelsComponent',
    data: { selector: 'app-kxinsights-models', resource: 'bi_query', action: 'ML_MODELS' },
  },
  {
    // Built-in BI usage-billing dashboard (src/modules/kxinsights/usage-dashboard.ts).
    // Its own route so a DIRECT visit is RBAC-checked too: bi_query + USAGE_VIEW,
    // the same right the landing button and the gateway (BiQueryDecorator) check.
    path: 'bi-usage',
    component: 'KxInsightsViewerComponent',
    data: { selector: 'app-kxinsights-viewer', resource: 'bi_query', action: 'USAGE_VIEW', identifier: 'bi-usage' },
  },
  {
    path: ':identifier',
    component: 'KxInsightsViewerComponent',
    data: { selector: 'app-kxinsights-viewer', resource: 'bi_dashboard', action: 'VIEW' },
  },
];
